// Waitlist mutation hooks. Mirrors the golden-path signup mutations in shiftSignup.tsx
// (getClient -> useMutation -> invalidate related queries) so the table's Join/Leave actions behave
// exactly like the existing Sign Up / Cancel actions.
import { useState } from 'react';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useLocalizedText } from '../localizedText';
import { useSearchParams } from 'react-router-dom';
import {
  BulkLeaveWaitlistCommandOutput,
  GetWaitlistsByUserCommandOutput,
  JoinWaitlistCommandInput,
  JoinWaitlistCommandOutput,
} from '@amzn/red-velvet-api';
import { getEnabledFeatures, useFeatures } from '../features';
import { getClient } from '../../utils/getClient';
import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';

// Shared mutation-key prefix for the waitlist actions; the trailing 'join'/'leave' keeps them
// individually identifiable (like shiftSignup's createSignup/deleteSignup) while letting
// useWaitlistAlerts read both outcomes off one [prefix, eventId] filter — mirroring useSignupAlerts.
const WAITLIST_MUTATION_PREFIX = ['RedVelvetApi', 'waitlist'];

// GetWaitlistsByUser is paginated (cce-7112). Per-user waitlist cardinality is tiny by design, so we
// request a large page to make the exhaustive drain typically a single round trip. 100 stays well under
// the Lambda 6MB response cap even with rich event descriptions, and far above a realistic per-user
// count; the frontend still groups + paginates the cards client-side.
const WAITLIST_DRAIN_PAGE_SIZE = 100;

/**
 * Join the waitlist for a shift. `shiftId` is enforced from the hook arguments (it keys the mutation),
 * so callers only supply the optional complex-signup fields (tShirtSizeId / waiverAccepted /
 * questionAnswers) — guests are intentionally unsupported for waitlist joins.
 *
 * On success invalidates the event's shifts (capacity/derived state may change) and the user's
 * waitlist entries (so the ShiftTable/Waitlists tab reflect the new ON_THE_WAITLIST membership once
 * GetWaitlistsByUser is wired — cce-7112).
 */
export function useJoinWaitlist(eventId: string, shiftId: string) {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));
  const queryClient = useQueryClient();

  return useMutation<JoinWaitlistCommandOutput, string, Omit<JoinWaitlistCommandInput, 'shiftId'>>({
    mutationKey: [...WAITLIST_MUTATION_PREFIX, eventId, shiftId, 'join'],
    mutationFn: (request) => client.joinWaitlist({ ...request, shiftId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['RedVelvetApi', 'getShiftsByEvent', { eventId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['RedVelvetApi', 'getWaitlistsByUser'],
      });
    },
  });
}

/**
 * Leave the waitlist for a single shift. Uses the bulk operation with a one-entry batch (the only
 * client op available — there is no single-leave endpoint), enforcing `shiftId` from the hook args
 * so the caller supplies only the `waitlistEntryId`.
 *
 * bulkLeaveWaitlist returns 200 with per-entry results; for a single entry a returned failure means
 * the leave did not happen, so we throw to surface it as an error (instead of a false success) and
 * skip the invalidation. On success invalidates the event's shifts and the user's waitlist entries,
 * mirroring useJoinWaitlist so the ShiftTable/Waitlists tab drop the row once it is gone.
 */
export function useBulkLeaveWaitlist(eventId: string, shiftId: string) {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));
  const queryClient = useQueryClient();

  return useMutation<BulkLeaveWaitlistCommandOutput, Error, { waitlistEntryId: string }>({
    mutationKey: [...WAITLIST_MUTATION_PREFIX, eventId, shiftId, 'leave'],
    mutationFn: async ({ waitlistEntryId }) => {
      const response = await client.bulkLeaveWaitlist({
        entries: [{ shiftId, waitlistEntryIds: [waitlistEntryId] }],
      });
      // failedDeletedWaitlistEntries is @required in the model, but the generated client types
      // output lists defensively; guard so a missing list can never throw on .find().
      const failure = (response.failedDeletedWaitlistEntries ?? []).find(
        (entry) => entry.waitlistEntryId === waitlistEntryId
      );
      if (failure) {
        throw new Error(failure.reason);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['RedVelvetApi', 'getShiftsByEvent', { eventId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['RedVelvetApi', 'getWaitlistsByUser'],
      });
    },
  });
}

/**
 * Fetch all of the current user's waitlist entries (each enriched with its event + shift) by draining
 * every page of the paginated GetWaitlistsByUser operation (cce-7112). useAllPagesRedVelvetQuery treats
 * the drained pages as one cache unit under the ['RedVelvetApi', 'getWaitlistsByUser', ...] key, which
 * useJoinWaitlist invalidates by prefix on success. `data` is the flat WaitlistEntryWithEvent[]; the
 * Waitlists tab groups by event and paginates the card list client-side.
 */
export function useGetAllWaitlistsByUser() {
  return useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getWaitlistsByUser', { pageSize: WAITLIST_DRAIN_PAGE_SIZE }],
    // The async auto-signup pipeline transitions entries ACTIVE -> SIGNED_UP server-side with no
    // client mutation to invalidate this drain, so refetch on focus ('always' — the app-wide
    // staleTime of Infinity means plain `true` would never fire). Covers the volunteer returning
    // to the tab after the promotion email.
    refetchOnWindowFocus: 'always',
    converter: (output: GetWaitlistsByUserCommandOutput) => ({
      items: output.waitlistEntries ?? [],
      next: output.page,
    }),
  });
}

type WaitlistAction = 'join' | 'leave';

/**
 * Page-level success/error alerts for the waitlist Join/Leave actions, mirroring useSignupAlerts:
 * reads the most recent join/leave mutation outcome for the event off the shared mutation key — so it
 * surfaces failures from the inline Join button, the complex-join dialog, and the Leave confirm modal
 * alike — and returns a dismissible alert. Those controls close without an inline error, so this is the
 * single place a waitlist outcome is shown.
 */
export function useWaitlistAlerts(eventId: string) {
  const { t } = useLocalizedText(['translation']);
  const [dismissedAt, setDismissedAt] = useState(0);

  const outcomes = useMutationState({
    filters: {
      mutationKey: [...WAITLIST_MUTATION_PREFIX, eventId],
      predicate: (mutation) =>
        mutation.state.status === 'success' || mutation.state.status === 'error',
    },
    select: (mutation) => {
      const mutationKey = mutation.options.mutationKey ?? [];
      return {
        status: mutation.state.status,
        timestamp: mutation.state.submittedAt,
        action: (mutationKey[mutationKey.length - 1] as WaitlistAction) ?? 'join',
      };
    },
  });

  const onDismiss = () => setDismissedAt(Date.now());
  const latest = outcomes[outcomes.length - 1];
  if (!latest || latest.timestamp < dismissedAt) {
    return { alert: undefined, onDismiss };
  }

  const isSuccess = latest.status === 'success';
  return {
    alert: {
      type: isSuccess ? ('success' as const) : ('error' as const),
      message: t(
        `eventDetails.shiftDisplay.waitlistAlerts.${latest.action}Waitlist${isSuccess ? 'Success' : 'Error'}`
      ),
    },
    onDismiss,
  };
}
